import { useState } from 'preact/hooks';
import { BtnPrimary } from '@components/buttons';
import { BtnCancel } from '@components/dialog';
import { useIntl } from 'shared/intl/use-intl';
import { isBareLink, linkToEmbed } from './embed-links';

type Props = {
  onBack(): void;
  onInsert(content: string): void;
};

export function EmbedForm({ onBack, onInsert }: Props) {
  const intl = useIntl();
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const trimmed = content.trim();

  return (
    <form
      class="flex flex-col min-h-0"
      onSubmit={(e) => {
        e.preventDefault();
        if (!trimmed) {
          return;
        }
        const embed = linkToEmbed(trimmed);
        if (embed) {
          onInsert(embed);
        } else if (isBareLink(trimmed)) {
          setError(
            intl(
              'This link can\'t be embedded. Paste its embed code instead, from the site\'s "Share → Embed" option.',
            ),
          );
        } else {
          onInsert(trimmed);
        }
      }}
    >
      <div class="p-6 overflow-auto">
        <label for="add-media-embed" class="block text-sm font-semibold text-gray-900 mb-1">
          {intl('Paste a link or embed code')}
        </label>
        <p id="add-media-embed-help" class="text-sm text-gray-500 mb-2.5">
          {intl(
            'Paste a YouTube, Vimeo or Loom link, or the <iframe> code from any site\'s "Share → Embed" option.',
          )}
        </p>
        <textarea
          id="add-media-embed"
          aria-describedby="add-media-embed-help"
          aria-invalid={error ? true : undefined}
          class="w-full min-h-36 rounded-lg border-gray-300 font-mono text-sm"
          value={content}
          onInput={(e) => {
            setContent(e.currentTarget.value);
            setError(undefined);
          }}
          autoFocus
        />
        {error && (
          <p class="mt-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
      <footer class="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
        <BtnCancel onClick={onBack}>{intl('Back')}</BtnCancel>
        <BtnPrimary class="rounded-full px-6" disabled={!trimmed}>
          {intl('Insert')}
        </BtnPrimary>
      </footer>
    </form>
  );
}
